import { Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

export default function CookiePolicy() {
  useSEO({
    title: 'Cookie Policy | GreenTick AI - How We Use Cookies',
    description: 'Understand how GreenTick AI uses cookies and tracking technologies to enhance your experience and provide secure, intelligent services.',
    canonicalPath: '/cookie-policy',
  });
  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <main className="pt-32 pb-20 relative overflow-hidden home-section-bg">
        {/* Global Grid Overlay */}
        <div className="absolute inset-0 bg-grid-pattern opacity-60 z-0 pointer-events-none"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8">
              <span>← Back to Home</span>
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center">
                <Cookie className="w-8 h-8 text-teal-400" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white">
                Cookie Policy
              </h1>
            </div>

            <p className="text-slate-400 mb-12">Last Updated: January 2026</p>

            <div className="space-y-8">
              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">What Are Cookies?</h2>
                <p className="text-slate-300 leading-relaxed">
                  Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences, analyzing how you use our service, and improving our platform.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Types of Cookies We Use</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Essential Cookies</h3>
                    <p className="text-slate-300 leading-relaxed">
                      These cookies are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility. You cannot opt-out of these cookies.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Performance Cookies</h3>
                    <p className="text-slate-300 leading-relaxed">
                      These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve the way our website works.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Functional Cookies</h3>
                    <p className="text-slate-300 leading-relaxed">
                      These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Targeting Cookies</h3>
                    <p className="text-slate-300 leading-relaxed">
                      These cookies may be set through our site by our advertising partners. They may be used to build a profile of your interests and show you relevant content on other sites.
                    </p>
                  </div>
                </div>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">How We Use Cookies</h2>
                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>We use cookies to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Keep you signed in to your account</li>
                    <li>Remember your preferences and settings</li>
                    <li>Understand how you use our platform</li>
                    <li>Improve our services and user experience</li>
                    <li>Provide personalized content and recommendations</li>
                    <li>Analyze traffic and usage patterns</li>
                    <li>Prevent fraudulent activity and improve security</li>
                  </ul>
                </div>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Third-Party Cookies</h2>
                <p className="text-slate-300 leading-relaxed">
                  We may use third-party services that place cookies on your device to help us analyze our service usage, provide analytics, and deliver relevant advertising. These third parties have their own privacy policies governing the use of your information.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Managing Cookies</h2>
                <div className="space-y-4 text-slate-300 leading-relaxed">
                  <p>You can control and manage cookies in various ways:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Most browsers allow you to block or delete cookies through their settings</li>
                    <li>You can set your browser to refuse all cookies or indicate when a cookie is being sent</li>
                    <li>You can use browser plugins to manage cookies</li>
                    <li>You can opt-out of targeted advertising cookies through industry opt-out platforms</li>
                  </ul>
                  <p className="mt-4">
                    Please note that blocking or deleting cookies may affect your user experience and some features of our service may not function properly.
                  </p>
                </div>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Cookie Retention</h2>
                <p className="text-slate-300 leading-relaxed">
                  Session cookies are temporary and are deleted when you close your browser. Persistent cookies remain on your device until they expire or you delete them. The retention period depends on the purpose of the cookie.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">Updates to This Policy</h2>
                <p className="text-slate-300 leading-relaxed">
                  We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We encourage you to review this policy periodically.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-bold text-white mb-4">More Information</h2>
                <p className="text-slate-300 leading-relaxed">
                  For more information about how we handle your data, please see our <Link to="/privacy_policy" className="text-teal-400 hover:text-teal-300">Privacy Policy</Link>. If you have questions about our use of cookies, please contact us at{' '}
                  <a href="mailto:privacy@greentick.org.in" className="text-teal-400 hover:text-teal-300">
                    privacy@greentick.org.in
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
